import {
  PUBLIC_CONTRACT_PAYMENT_STATUSES,
  PUBLIC_CONTRACT_STATUSES,
  PublicContract,
  PublicContractItem,
  PublicContractPaymentStatus,
  PublicContractStatus,
} from "../../../redux/features/types/publicContractTypes";
import { formatNumberWithCommas } from "../../../helpers";

export const PUBLIC_CONTRACT_STATUS_LABELS: Record<string, string> = {
  PENDING: "Pending",
  PAID: "Paid",
  SHIPED: "Shipped",
  COMPLETED: "Completed",
  ABANDONED: "Abandoned",
  EXPIRED: "Expired",
  CANCELED: "Canceled",
};

export const PUBLIC_CONTRACT_PAYMENT_LABELS: Record<string, string> = {
  PENDING: "Pending",
  PAID: "Paid",
  FAILED: "Failed",
};

export const labelPublicContractStatus = (status?: string | null) =>
  PUBLIC_CONTRACT_STATUS_LABELS[status ?? ""] ??
  (status
    ? status.charAt(0) + status.slice(1).toLowerCase()
    : "—");

export const labelPaymentStatus = (status?: string | null) =>
  PUBLIC_CONTRACT_PAYMENT_LABELS[status ?? ""] ??
  (status
    ? status.charAt(0) + status.slice(1).toLowerCase()
    : "—");

export const publicContractStatusOptions = [
  { label: "All", value: null },
  ...PUBLIC_CONTRACT_STATUSES.map((value) => ({
    label: labelPublicContractStatus(value),
    value,
  })),
];

export const publicContractPaymentOptions = [
  { label: "All", value: null },
  ...PUBLIC_CONTRACT_PAYMENT_STATUSES.map((value) => ({
    label: labelPaymentStatus(value),
    value,
  })),
];

export const publicContractStatusUpdateOptions = PUBLIC_CONTRACT_STATUSES.map(
  (value) => ({
    label: labelPublicContractStatus(value),
    value,
  })
);

export const getPublicContractStatusClasses = (status?: string) => {
  switch (status) {
    case "PENDING":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "PAID":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "SHIPED":
      return "bg-indigo-100 text-indigo-700 border-indigo-200";
    case "COMPLETED":
      return "bg-green-100 text-green-700 border-green-200";
    case "ABANDONED":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "EXPIRED":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "CANCELED":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export const getPaymentStatusClasses = (status?: string) => {
  switch (status) {
    case "PAID":
      return "bg-green-100 text-green-700 border-green-200";
    case "FAILED":
      return "bg-red-100 text-red-700 border-red-200";
    case "PENDING":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export const formatNaira = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `₦${formatNumberWithCommas(n)}`;
};

export const formatYmd = (value?: Date | string | null) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const formatYmdEndInclusive = (value?: Date | string | null) => {
  if (!value) return "";
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/** Treat API placeholders like "-", "—", "N/A", "null" as empty. */
export const cleanText = (value?: unknown): string => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (
    text === "-" ||
    text === "—" ||
    text === "–" ||
    lower === "n/a" ||
    lower === "na" ||
    lower === "null" ||
    lower === "undefined"
  ) {
    return "";
  }
  return text;
};

export const getContractCode = (contract?: PublicContract | null) =>
  cleanText(contract?.contractCode) || cleanText(contract?.code) || "—";

const looksLikeId = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  ) || /^\d+$/.test(value);

/** Prefer a real id string; ignore empty / non-id junk. */
const pickId = (...candidates: unknown[]) => {
  for (const c of candidates) {
    if (typeof c !== "string") continue;
    const text = cleanText(c);
    if (text && looksLikeId(text)) return text;
  }
  return "";
};

/**
 * Normalize API person shapes into a single object with an `id` when possible.
 * Handles: nested user object, string UUID on `*Id`, or details-only `buyerInfo`.
 */
const normalizePerson = (person: any, idOrPerson?: any) => {
  const fromPerson =
    person && typeof person === "object" ? { ...person } : null;
  const fromIdField =
    idOrPerson && typeof idOrPerson === "object" ? { ...idOrPerson } : null;

  if (!fromPerson && !fromIdField) {
    const onlyId = pickId(idOrPerson);
    return onlyId ? { id: onlyId } : null;
  }

  const merged = { ...(fromIdField || {}), ...(fromPerson || {}) };
  const id = pickId(
    merged.id,
    typeof idOrPerson === "string" ? idOrPerson : null,
    merged.userId,
    merged.buyerId,
    merged.vendorId
  );

  if (id) merged.id = id;
  return Object.keys(merged).length ? merged : null;
};

export const getVendorUser = (contract?: PublicContract | null) => {
  if (!contract) return null;
  return normalizePerson(contract.vendor, contract.vendorId);
};

export const getBuyerUser = (contract?: PublicContract | null) => {
  if (!contract) return null;
  // Prefer full buyer relation; fall back to buyerInfo + buyerId (auto-created
  // guest accounts often arrive that way on public-contract payloads).
  return (
    normalizePerson(contract.buyer, contract.buyerId) ||
    normalizePerson(contract.buyerInfo, contract.buyerId)
  );
};

export const getPersonDisplayName = (person?: any | null) => {
  if (!person || typeof person !== "object") return "";
  const first = cleanText(person.firstName);
  const last = cleanText(person.lastName);
  const composed = [first, last].filter(Boolean).join(" ");
  // Prefer composed first/last so API placeholders in fullName (e.g. "John -")
  // or fullName: "-" don't win over a real first name.
  if (composed) return composed;

  const full = cleanText(person.fullName);
  // Strip trailing placeholder last-name markers: "Jane -" / "Jane —"
  const cleanedFull = full.replace(/\s+[-—–]\s*$/, "").trim();
  return (
    cleanedFull ||
    cleanText(person.businessName) ||
    cleanText(person.email) ||
    ""
  );
};

export const getBuyerDisplayName = (contract?: PublicContract | null) =>
  getPersonDisplayName(getBuyerUser(contract)) || "—";

export const getVendorDisplayName = (contract?: PublicContract | null) =>
  getPersonDisplayName(getVendorUser(contract)) || "—";

export const getContractItems = (
  contract?: PublicContract | null
): PublicContractItem[] => {
  const items =
    contract?.contractItems ??
    contract?.contractItem ??
    contract?.items ??
    contract?.products ??
    [];
  return Array.isArray(items) ? items : [];
};

/** Resolve nested product object from `product` or `productId` (orders use productId). */
export const getItemProduct = (item?: PublicContractItem | null) => {
  if (!item) return null;
  if (item.product && typeof item.product === "object") return item.product;
  if (item.productId && typeof item.productId === "object") return item.productId;
  return null;
};

export const getItemProductId = (item?: PublicContractItem | null) => {
  if (!item) return "";
  const nested = getItemProduct(item);
  return (
    pickId(nested?.id) ||
    pickId(typeof item.productId === "string" ? item.productId : null) ||
    pickId(typeof item.product === "string" ? item.product : null) ||
    ""
  );
};

export const getItemName = (item: PublicContractItem) => {
  const product = getItemProduct(item);
  return (
    cleanText(item?.name) ||
    cleanText(item?.productName) ||
    cleanText(product?.name) ||
    cleanText(product?.title) ||
    "Untitled item"
  );
};

export const getItemImage = (item?: PublicContractItem | null) => {
  if (!item) return "";
  const product = getItemProduct(item);
  const photos = item.photos ?? product?.photos;
  if (Array.isArray(photos) && photos.length && cleanText(photos[0])) {
    return String(photos[0]);
  }
  return "";
};

export const getItemUnitPrice = (item: PublicContractItem) => {
  const product = getItemProduct(item);
  const value = item.vendorDiscountedPrice ?? item.price ?? product?.price;
  if (value == null) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

export const getItemQuantity = (item: PublicContractItem) =>
  item?.quantity ?? item?.productQuantity ?? null;

export const getPlaceName = (value: any) => {
  if (!value) return "";
  if (typeof value === "string") {
    const text = cleanText(value);
    // Bare IDs (UUID / numeric) are not useful place labels.
    if (!text || looksLikeId(text)) return "";
    return text;
  }
  return (
    cleanText(value.name) ||
    cleanText(value.title) ||
    cleanText(value.stateName) ||
    cleanText(value.cityName) ||
    ""
  );
};

export const getContractTotal = (contract?: PublicContract | null) => {
  if (!contract) return null;
  if (contract.totalAmount != null) return contract.totalAmount;
  if (contract.amount != null) return contract.amount;
  const itemsTotal = getContractItems(contract).reduce((sum, item) => {
    const unit = getItemUnitPrice(item) ?? 0;
    const qty = Number(getItemQuantity(item) ?? 0);
    const line =
      item.totalCost != null ? Number(item.totalCost) : unit * qty;
    return sum + (Number.isNaN(line) ? 0 : line);
  }, 0);
  const delivery = Number(contract.deliveryCost) || 0;
  if (!itemsTotal && !delivery) return contract.itemsAmount ?? null;
  return itemsTotal + delivery;
};

export type StatusTab = {
  id: string;
  name: string;
  value: PublicContractStatus | "ALL";
};

export const PUBLIC_CONTRACT_TABS: StatusTab[] = [
  { id: "ALL", name: "All", value: "ALL" },
  ...PUBLIC_CONTRACT_STATUSES.map((status) => ({
    id: status,
    name: labelPublicContractStatus(status),
    value: status,
  })),
];

export const isPublicContractStatus = (
  value: string
): value is PublicContractStatus =>
  (PUBLIC_CONTRACT_STATUSES as readonly string[]).includes(value);

export const isPaymentStatus = (
  value: string
): value is PublicContractPaymentStatus =>
  (PUBLIC_CONTRACT_PAYMENT_STATUSES as readonly string[]).includes(value);
